<template>
  <q-dialog v-model="showDialog" persistent class="create-modal">
    <q-card flat class="create-modal-card main-shadow light-bordered medium-radius q-px-none">
      <q-card-section class="sticky-section header row items-center border-bottom q-pb-md q-pr-sm">
        <h5 class="text-primary-dark text-font-medium">{{ modalTitle }}</h5>
        <q-space />
        <q-btn v-close-popup icon="close" flat round dense color="secondary" @click="onCancel" />
      </q-card-section>

      <div class="scrollable-content">
        <q-card-section
          v-if="createForm.currentStep !== CREATE_MODAL_STEPS.RESULTS"
          class="search-container relative border-bottom q-pt-lg q-pb-sm"
        >
          <search-input-section
            v-model:input-value="createForm.entityName"
            :type="props.type"
            :is-loading="isLoading"
            :is-suggesting="isSuggesting"
            @search="onSuggest"
          />
        </q-card-section>

        <q-card-section
          v-if="createForm.currentStep === CREATE_MODAL_STEPS.LOADING"
          class="loading-step q-pa-lg"
        >
          <div class="text-center">
            <q-spinner color="brand" size="2rem" />
            <p class="q-mt-md text-16 text-secondary">Searching with AI magic...</p>
          </div>
        </q-card-section>

        <q-card-section
          v-if="createForm.currentStep === CREATE_MODAL_STEPS.RESULTS"
          class="results-step q-pt-md q-pb-sm"
        >
          <p class="results-subtitle q-mb-md text-16 text-primary text-font-medium">
            How would you like to continue?
          </p>

          <div
            v-if="suggestionError"
            class="results-error row items-center no-wrap q-mb-md text-14 text-warning"
          >
            <q-icon name="search_off" size="1.5rem" class="q-mr-sm" />
            <p class="q-mb-none text-font-medium">{{ suggestionError }}</p>
          </div>

          <div class="option-section">
            <header class="option-section__label row items-center text-14 text-secondary">
              <q-icon name="auto_awesome" size="1.1rem" class="q-mr-xs" />
              <span>With AI Suggestion</span>
            </header>
            <create-modal-selectable-option
              v-model:full-ai-assessment="createForm.fullAiAssessment"
              v-model:start-subprocessor-list="createForm.startSubprocessorList"
              :value="aiSuggestion"
              empty-text="No AI suggestion for this name."
              :selected="createForm.selectedOption === 'ai'"
              :disabled="!aiSuggestion"
              :is-loading="isLoading"
              @select="() => onOptionSelect('ai')"
            />
          </div>

          <div class="option-section">
            <header class="option-section__label row items-center text-14 text-secondary">
              <q-icon name="edit" size="1.1rem" class="q-mr-xs" />
              <span>With custom name</span>
            </header>
            <create-modal-selectable-option
              v-model:full-ai-assessment="createForm.fullAiAssessment"
              v-model:start-subprocessor-list="createForm.startSubprocessorList"
              :value="createForm.entityName ? `'${createForm.entityName}'` : null"
              :selected="createForm.selectedOption === 'custom'"
              :disabled="!createForm.entityName"
              :is-loading="isLoading"
              @select="() => onOptionSelect('custom')"
            />
          </div>

          <create-modal-existing-companies
            class="q-mt-md"
            :companies="companiesInDb"
            :selected-company-id="createForm.selectedExistingCompanyId"
            @select-company="onExistingSelect"
          />
        </q-card-section>
      </div>

      <div class="sticky-section footer">
        <modal-footer-actions
          :type="props.type"
          :input-value="createForm.entityName"
          :current-step="createForm.currentStep"
          :is-loading="isLoading"
          :is-suggesting="isSuggesting"
          :primary-action-label="primaryActionLabel"
          :primary-action-disabled="!createForm.selectedOption"
          @cancel="onCancel"
          @search="onSuggest"
          @submit="onSubmit"
          @commit="onCommit"
          @search-again="onSearchAgain"
        />
      </div>
    </q-card>
  </q-dialog>
</template>

<script setup>
import { reactive, computed, watch, nextTick } from 'vue'
import { useRoute, useRouter } from 'vue-router'

import { useSuggestCompanyMutation } from 'src/composables/company/useSuggestCompanyMutation'

import { log } from 'src/services/app/log.service'
import {
  CREATE_MODAL_STEPS,
  COMPANY_DETAILS_TABS,
  initialCreateModalFormData,
} from 'src/services/consts/company.const'
import { ROUTES } from 'src/services/consts/routes.const'

import SearchInputSection from 'src/components/common/SearchInputSection.vue'
import CreateModalSelectableOption from 'src/components/common/CreateModalSelectableOption.vue'
import CreateModalExistingCompanies from 'src/components/common/CreateModalExistingCompanies.vue'
import ModalFooterActions from 'src/components/common/CreateModalFooterActions.vue'

const emit = defineEmits(['update:show'])
const props = defineProps({
  show: {
    type: Boolean,
    default: false,
  },
  type: {
    type: String,
    default: 'company',
    validator: (value) => ['company', 'product'].includes(value),
  },
  companyId: {
    type: String,
    default: null,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  // Callback function that returns a promise
  onSubmitCallback: {
    type: Function,
    required: true,
  },
  prefillName: {
    type: String,
    default: '',
  },
  requestContext: {
    type: Object,
    default: null,
  },
  onSelectExistingCallback: {
    type: Function,
    default: null,
  },
})

const router = useRouter()
const route = useRoute()
const createForm = reactive({ ...initialCreateModalFormData })
const {
  isSuggesting,
  suggestionError,
  aiSuggestion,
  companiesInDb,
  suggestCompany,
  resetSuggestCompany,
} = useSuggestCompanyMutation()

const hasRequestContext = computed(() => !!props.requestContext)
const hasSelectExistingCallback = computed(
  () => typeof props.onSelectExistingCallback === 'function',
)
const selectedExistingCompany = computed(() =>
  createForm.selectedOption === 'existing' && createForm.selectedExistingCompanyId
    ? companiesInDb.value.find((c) => c.company_id === createForm.selectedExistingCompanyId)
    : null,
)
const primaryActionLabel = computed(() => {
  const sel = createForm.selectedOption
  if (!sel) return 'Continue'
  if (sel === 'existing') {
    if (hasRequestContext.value) return 'Approve request'
    if (hasSelectExistingCallback.value) return 'Link company'
    return 'View company'
  }
  return hasRequestContext.value ? 'Approve & Create' : 'Create company'
})
const modalTitle = computed(() => {
  if (hasRequestContext.value && props.type === 'company') return 'Approve creation request'
  return props.type === 'company' ? 'Create a new company' : 'Create a new product'
})
const showDialog = computed({
  get: () => props.show,
  set: (value) => emit('update:show', value),
})

function resetCreateForm() {
  Object.assign(createForm, { ...initialCreateModalFormData })
}

const onOptionSelect = (option) => {
  createForm.selectedOption = option
  createForm.selectedExistingCompanyId = null
}

const onExistingSelect = (company) => {
  createForm.selectedOption = 'existing'
  createForm.selectedExistingCompanyId = company.company_id
}
const onCancel = () => {
  resetCreateForm()
  resetSuggestCompany()
  emit('update:show', false)
}
const onSuggest = async () => {
  if (!createForm.entityName || props.type !== 'company') return

  createForm.fullAiAssessment = false
  createForm.selectedOption = null
  createForm.selectedExistingCompanyId = null
  createForm.currentStep = CREATE_MODAL_STEPS.LOADING

  try {
    await suggestCompany(createForm.entityName)
    createForm.currentStep = CREATE_MODAL_STEPS.RESULTS
  } catch (error) {
    log.error(`Error suggesting ${props.type}:`, error)
    createForm.currentStep = CREATE_MODAL_STEPS.RESULTS
  }
}
const onNavigateToCompany = (company) => {
  router.push({
    name: ROUTES.COMPANY_INFO,
    params: { id: company.company_id, tab: COMPANY_DETAILS_TABS.INFO },
  })
  onCancel()
}
const onSelectExisting = async (company) => {
  if (!props.onSelectExistingCallback) {
    onNavigateToCompany(company)
    return
  }
  try {
    await props.onSelectExistingCallback(company)
    onCancel()
  } catch (error) {
    log.error('Error approving with existing company:', error)
  }
}
const onCommit = async () => {
  const sel = createForm.selectedOption
  if (!sel) return

  if (sel === 'existing') {
    const company = selectedExistingCompany.value
    if (!company) return
    if (hasSelectExistingCallback.value) {
      await onSelectExisting(company)
    } else {
      onNavigateToCompany(company)
    }
    return
  }

  await onSubmit({ useSuggestion: sel === 'ai' })
}
const onSearchAgain = () => {
  createForm.currentStep = CREATE_MODAL_STEPS.INPUT
  createForm.selectedOption = null
  createForm.selectedExistingCompanyId = null
  resetSuggestCompany()
}
const onSubmit = async (payload = {}) => {
  const { useSuggestion = false } = payload

  if (props.type === 'company' && useSuggestion && !aiSuggestion.value && !suggestionError.value)
    return
  if (!createForm.entityName) return

  await createEntity(payload)
}
const createEntity = async (payload = {}) => {
  const { useSuggestion = false } = payload

  try {
    let companyName = createForm.entityName

    if (useSuggestion && aiSuggestion.value) {
      companyName = aiSuggestion.value
    }

    await props.onSubmitCallback({
      type: props.type,
      name: companyName,
      companyId: props.companyId,
      fullAiAssessment: createForm.fullAiAssessment,
      startSubprocessorList: createForm.startSubprocessorList,
    })

    resetCreateForm()
    resetSuggestCompany()
    emit('update:show', false)
  } catch (error) {
    log.error(`Error creating ${props.type}:`, error)
  }
}

watch(
  () => route.query,
  async (query) => {
    if (props.requestContext) return
    if (query.createCompany) {
      createForm.entityName = route.query.txt || ''
      await nextTick()
      const nextQuery = { ...route.query }
      delete nextQuery.createCompany
      router.replace({ ...route, query: nextQuery })
    }
  },
)
watch(
  () => props.show,
  (newShow) => {
    if (newShow && props.prefillName) {
      createForm.entityName = props.prefillName
    }
  },
)
</script>

<style lang="scss">
@import '/src/css/setup/variables.scss';

.create-modal {
  z-index: 30000;

  .create-modal-card {
    width: 800px;
    max-width: 800px;
    min-height: 300px;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
    transition: all 0.3s ease-in-out;

    .sticky-section {
      position: sticky;
      z-index: 10;
      background-color: white;
      &.header {
        border-bottom: 1px solid $clr-border;
        top: 0;
      }
      &.footer {
        border-top: 1px solid $clr-border;
        bottom: 0;
      }
    }

    .scrollable-content {
      flex: 1;
      overflow-y: auto;
      min-height: 0;

      transition: all 0.3s ease-in-out;

      display: flex;
      flex-direction: column;
      justify-content: flex-start;
    }

    .loading-step {
      display: flex;
      align-items: center;
      justify-content: center;
    }

    .option-section {
      display: flex;
      flex-direction: column;
      gap: 0.25rem;

      & + .option-section {
        margin-top: 0.75rem;
      }
    }
  }
}
</style>
